<template>
  <v-container fluid class="create-article-container">
    <v-card class="mx-auto quill-card" max-width="1080">
      <v-card-title>Create Handbook Article</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="submitArticle">
          <v-alert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ formError }}
          </v-alert>
          <!-- Category -->
          <v-select
            v-model="form.category"
            :items="categories"
            item-title="name"
            item-value="id"
            return-object
            label="Category"
            required
          />
          <!-- Title -->
          <v-text-field
            v-model="form.title"
            label="Title"
            required
            :error-messages="errors.title"
            :disabled="saving"
          />
          <!-- Description -->
          <v-textarea
            v-model="form.description"
            label="Short description (shown on dashboard)"
            counter="255"
            auto-grow
          />
          <!-- Tiptap Editor -->
          <EditorView :editor="editor" />
          <!-- Pinned & Onboarding Fields -->
          <div class="checkbox-group">
            <v-checkbox
              v-model="form.visible_to_onboarding"
              label="Visible to onboarding"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="button-group">
        <v-btn color="primary" :loading="saving" :disabled="saving" @click="submitArticle">Save Article</v-btn>
        <v-btn text @click="$router.push('/handbook')">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import api from "@/plugins/axios";
import { createEditor } from '@/tiptap/components/EditorCore.js'
import EditorView from '@/tiptap/components/EditorView.vue'
import { normalizeForSave } from '@/tiptap/diff/normalizeForSave'

export default {
  name: "HandbookArticleCreate",
  components: {
    EditorView
  },
  data() {
    return {
      form: {
        category: null,
        title: "",
        description: "",
        visible_to_onboarding: false,
        content_json: null,
      },
      categories: [],
      articles: [],
      errors: { title: [] },
      formError: '',
      saving: false,
      editor: null,
    };
  },
  created() {
    this.editor = createEditor({
      content: '<p></p>',
      features: {
        tables: true,
        images: true,
        video: true,
        links: true,
        codeBlock: true,
        quote: true,
        highlight: true,
        color: true,
        strike: true,
        bulletList: true,
        orderedList: true,
        taskList: true,
        indent: true,
        horizontalRule: true,
        mentions: true,        // handbook #mentions ON
        userMentions: false,    // forum @mentions OFF here
        heading: true,
        fontFamily: true,
        fontSize: true,
        textAlign: true,
        clear: true,
        subSuper: true,
      },
    })
  },
  beforeUnmount() {
    try {
      if (this.editor?.view?.docView) {
        this.editor.destroy();
      }
    } catch (e) {
      console.debug('Editor destroy skipped:', e);
    }
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await api.get("/api/handbook-categories/");
        this.categories = res.data;
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    async fetchArticles() {
      try {
        const res = await api.get("/api/handbook-sections/suggestions/");
        this.articles = res.data;
      } catch (error) {
        console.error("Error fetching articles:", error);
      }
    },
    async submitArticle() {
      this.errors = { title: [] };
      this.formError = '';
      this.saving = true;
      try {
        const tiptapEditor = this.editor
        if (!tiptapEditor) {
          this.formError = 'Editor not ready.'
          this.saving = false
          return
        }

        // Wait for any pending DOM updates (prevents null view.docView)
        await this.$nextTick();

        let html = '';
        let text = '';
        let json = null;

        try {
          html = tiptapEditor.getHTML();
          const rawJson = tiptapEditor.getJSON();
          json = normalizeForSave(rawJson);
          text = tiptapEditor.getText();
        } catch (e) {
          console.warn('[tiptap extract failed]', e);
          this.formError = 'Editor content not ready. Please try again.';
          this.saving = false;
          return;
        }

        const categoryId =
          this.form.category && typeof this.form.category === 'object'
            ? this.form.category.id
            : this.form.category;

        // Resolve active bar id (fallbacks for superusers); let backend default if absent
        const barId =
          this.form.bar ??
          this.$store?.state?.bar?.id ??
          this.$store?.state?.currentBar?.id ??
          this.$store?.getters?.['bar/currentId'] ??
          null

        const payload = {
          ...this.form,
          ...(barId ? { bar: barId } : {}), // include bar only if found; backend will attach otherwise
          category: categoryId,
          content_json: json,
          content_html: html,
          content_text: text,
          content: html,
        };

        await api.post('/api/handbook-sections/', payload, { withCredentials: true });
        this.$router.push('/handbook');
      } catch (err) {
        const status = err && err.response ? err.response.status : undefined;
        const data = err && err.response ? (err.response.data || {}) : {};
        console.error('[handbook save] status:', status, 'data:', data);
        if (data && data.title) {
          this.errors.title = Array.isArray(data.title) ? data.title : [String(data.title)];
        } else if (data && data.category) {
          this.formError = Array.isArray(data.category) ? data.category.join(' ') : String(data.category);
        } else if (data && data.bar) {
          this.formError = Array.isArray(data.bar) ? data.bar.join(' ') : String(data.bar);
        } else if (data && data.detail) {
          this.formError = String(data.detail);
        } else if (data && data.non_field_errors) {
          const arr = Array.isArray(data.non_field_errors) ? data.non_field_errors : [String(data.non_field_errors)];
          this.formError = arr.join(' ');
        } else if (status === 400) {
          this.formError = 'Bad request: ' + JSON.stringify(data);
        } else {
          this.formError = 'Failed to save the article. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.fetchCategories();
    await this.fetchArticles();
  },
};
</script>

<style scoped>
.create-article-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 20px;
}
/* Allow popovers/menus to escape the card */
.quill-card {
  overflow: visible !important;
}
.ql-mention-list-container {
  border: 1px solid #ccc;
  background: #fff;
}
.checkbox-group {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 10px;
}
.button-group {
  display: flex;
  justify-content: flex-start;
  margin-top: -10px;
  padding-bottom: 10px;
}

/* You may want to add Tiptap-specific image constraints here if needed */
</style>
